<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import SimpleWhatsAppTextEditor from './SimpleWhatsAppTextEditor.vue';
import CampaignAudioRecorder from './CampaignAudioRecorder.vue';
import {
  inferCampaignMessageType,
  DEFAULT_AI_REWRITE_PROMPT,
} from '../utils/campaignForm';

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  total: {
    type: Number,
    required: true,
  },
  accountId: {
    type: [String, Number],
    default: '',
  },
  availableVariables: {
    type: Array,
    default: () => [],
  },
  availableVariableDetails: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update', 'remove', 'move-up', 'move-down', 'error']);

const { t } = useI18n();
const isUploadingFile = ref(false);
const isRecordingAudio = ref(false);
const recordingAudioDurationText = ref('00:00');
const audioRecorderInput = ref(null);
const fileInput = ref(null);

const canMoveUp = props.index > 0;
const canMoveDown = props.index < props.total - 1;
const currentMessageType = computed(() =>
  inferCampaignMessageType(props.message)
);
const hasAudioAttachment = computed(
  () => currentMessageType.value === 'audio' && Boolean(props.message.fileUrl)
);
const audioPreviewUrl = computed(() =>
  hasAudioAttachment.value ? props.message.fileUrl : ''
);
const textFieldLabel = computed(() =>
  props.message.fileName
    ? t('CAMPAIGN.ENHANCED.FIELDS.MESSAGE_CONTENT.ATTACHMENT_LABEL')
    : t('CAMPAIGN.ENHANCED.FIELDS.MESSAGE_CONTENT.LABEL')
);
const textFieldPlaceholder = computed(() =>
  props.message.fileName
    ? t('CAMPAIGN.ENHANCED.FIELDS.MESSAGE_CONTENT.ATTACHMENT_PLACEHOLDER')
    : t('CAMPAIGN.ENHANCED.FIELDS.MESSAGE_CONTENT.PLACEHOLDER')
);
const variablesHint = computed(() =>
  t('CAMPAIGN.ENHANCED.FIELDS.VARIABLES_HINT', {
    variables: props.availableVariables
      .map(variable => `{{${variable}}}`)
      .join(', '),
  })
);
const fileAccept = computed(() =>
  isRecordingAudio.value || currentMessageType.value === 'audio'
    ? 'audio/*,.mp3,.wav,.ogg,.opus,.m4a,.aac,.webm'
    : 'image/*,video/mp4,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt'
);
const aiRewriteEnabled = computed({
  get: () => Boolean(props.message.useAiGeneration),
  set: value => {
    emit('update', {
      useAiGeneration: value,
      aiRewritePrompt:
        props.message.aiRewritePrompt || DEFAULT_AI_REWRITE_PROMPT,
    });
  },
});

const updateField = (field, value) => {
  emit('update', { [field]: value });
};

const handleFileUpload = async event => {
  const file = event.target.files[0];
  if (!file) return;

  isUploadingFile.value = true;

  try {
    const { fileUrl, blobId } = await uploadFile(file, props.accountId);
    emit('update', {
      fileUrl,
      fileName: file.name,
      fileBlobId: blobId,
      fileMimeType: file.type,
      isRecordedAudio: false,
    });
  } catch (error) {
    emit(
      'error',
      error?.response?.data?.error ||
        t('CAMPAIGN.ENHANCED.ERRORS.UPLOAD_FAILED')
    );
  } finally {
    isUploadingFile.value = false;
    if (fileInput.value) {
      fileInput.value.value = '';
    }
  }
};

const removeFile = () => {
  isRecordingAudio.value = false;
  recordingAudioDurationText.value = '00:00';
  emit('update', {
    fileUrl: null,
    fileName: '',
    fileBlobId: null,
    fileMimeType: '',
    isRecordedAudio: false,
  });
};

const openFilePicker = () => {
  fileInput.value?.click();
};

const startAudioRecording = () => {
  removeFile();
  isRecordingAudio.value = true;
};

const stopAudioRecording = () => {
  audioRecorderInput.value?.stopRecording();
};

const cancelAudioRecording = () => {
  isRecordingAudio.value = false;
  recordingAudioDurationText.value = '00:00';
};

const onRecorderProgressChanged = duration => {
  recordingAudioDurationText.value = duration;
};

const onFinishRecorder = async recordedFile => {
  if (!recordedFile?.file) {
    cancelAudioRecording();
    return;
  }

  isUploadingFile.value = true;

  try {
    const { fileUrl, blobId } = await uploadFile(
      recordedFile.file,
      props.accountId
    );
    emit('update', {
      fileUrl,
      fileName: recordedFile.file.name,
      fileBlobId: blobId,
      fileMimeType: recordedFile.file.type,
      isRecordedAudio: true,
    });
  } catch (error) {
    emit(
      'error',
      error?.response?.data?.error ||
        t('CAMPAIGN.ENHANCED.ERRORS.UPLOAD_FAILED')
    );
  } finally {
    isUploadingFile.value = false;
    isRecordingAudio.value = false;
    recordingAudioDurationText.value = '00:00';
  }
};
</script>

<template>
  <div
    class="relative rounded-lg border border-n-slate-6 bg-n-white p-3 dark:bg-n-slate-3 md:p-4"
  >
    <Button
      variant="ghost"
      size="xs"
      color="ruby"
      type="button"
      icon="i-lucide-x"
      class="absolute right-2 top-2 z-10 rounded-full md:right-3 md:top-3"
      :aria-label="t('CAMPAIGN.ENHANCED.BUTTONS.REMOVE_MESSAGE')"
      :title="t('CAMPAIGN.ENHANCED.BUTTONS.REMOVE_MESSAGE')"
      :disabled="total === 1"
      @click="emit('remove')"
    />

    <div class="flex flex-col gap-3 md:flex-row md:items-start md:gap-4">
      <div class="flex items-center justify-between gap-3 md:block md:justify-start">
        <div
          class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-n-blue-9 font-medium text-white"
        >
          {{ index + 1 }}
        </div>

        <div class="flex gap-2 md:hidden">
          <Button
            variant="ghost"
            size="sm"
            type="button"
            icon="arrow-up"
            :disabled="!canMoveUp"
            @click="emit('move-up')"
          />
          <Button
            variant="ghost"
            size="sm"
            type="button"
            icon="arrow-down"
            :disabled="!canMoveDown"
            @click="emit('move-down')"
          />
        </div>
      </div>

      <div class="flex-1 space-y-4">
        <Input
          v-if="index > 0"
          :model-value="message.delayAfterSeconds"
          type="number"
          :label="t('CAMPAIGN.ENHANCED.FIELDS.DELAY_AFTER.LABEL')"
          :placeholder="t('CAMPAIGN.ENHANCED.FIELDS.DELAY_AFTER.PLACEHOLDER')"
          min="0"
          @update:model-value="updateField('delayAfterSeconds', $event)"
        />

        <SimpleWhatsAppTextEditor
          :model-value="message.content"
          :label="textFieldLabel"
          :placeholder="textFieldPlaceholder"
          :available-variables="availableVariables"
          :available-variable-details="availableVariableDetails"
          @update:model-value="updateField('content', $event)"
        />

        <div class="rounded bg-n-alpha-black2 p-2 text-xs text-n-slate-11">
          {{ variablesHint }}
        </div>

        <div class="rounded-lg border border-n-slate-6 p-3 space-y-3">
          <div class="flex items-start justify-between gap-4">
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.ENHANCED.FIELDS.AI_REWRITE.LABEL') }}
              </p>
              <p class="mt-1 text-xs text-n-slate-11">
                {{ t('CAMPAIGN.ENHANCED.FIELDS.AI_REWRITE.HELP') }}
              </p>
            </div>
            <Switch v-model="aiRewriteEnabled" />
          </div>

          <div v-if="aiRewriteEnabled" class="space-y-2">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.ENHANCED.FIELDS.AI_REWRITE.PROMPT_LABEL') }}
            </label>
            <textarea
              :value="message.aiRewritePrompt"
              rows="12"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent resize-y min-h-[16rem]"
              :placeholder="
                t('CAMPAIGN.ENHANCED.FIELDS.AI_REWRITE.PROMPT_PLACEHOLDER')
              "
              @input="updateField('aiRewritePrompt', $event.target.value)"
            />
          </div>
        </div>

        <div class="space-y-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.ENHANCED.FIELDS.FILE_UPLOAD.LABEL') }}
          </label>

          <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Button
              class="w-full"
              variant="outline"
              size="sm"
              type="button"
              icon="paperclip"
              :label="t('CAMPAIGN.ENHANCED.FIELDS.FILE_UPLOAD.ADD_FILE')"
              :disabled="isRecordingAudio || isUploadingFile"
              @click="openFilePicker"
            />
            <Button
              class="w-full"
              variant="outline"
              size="sm"
              type="button"
              icon="mic"
              :label="t('CAMPAIGN.ENHANCED.FIELDS.FILE_UPLOAD.RECORD_AUDIO')"
              :disabled="isRecordingAudio || isUploadingFile"
              @click="startAudioRecording"
            />
          </div>

          <div
            v-if="message.fileName"
            class="flex flex-col gap-1 rounded bg-n-alpha-black2 p-2 sm:flex-row sm:items-center sm:gap-2"
          >
            <span class="flex-1 truncate text-sm text-n-slate-12">{{
              message.fileName
            }}</span>
            <span
              v-if="message.isRecordedAudio"
              class="text-xs text-n-slate-11"
            >
              {{ t('CAMPAIGN.ENHANCED.FIELDS.FILE_UPLOAD.RECORDED_AUDIO') }}
            </span>
          </div>

          <div
            v-if="message.fileName && hasAudioAttachment"
            class="rounded-lg border border-n-slate-6 p-3 bg-n-alpha-black2"
          >
            <p class="text-xs text-n-slate-11 mb-2">
              {{ t('CAMPAIGN.ENHANCED.FIELDS.FILE_UPLOAD.AUDIO_PREVIEW') }}
            </p>
            <audio
              class="w-full"
              controls
              preload="metadata"
              :src="audioPreviewUrl"
            />
          </div>

          <div v-if="message.fileName" class="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              type="button"
              icon="trash"
              color="ruby"
              :label="t('CAMPAIGN.ENHANCED.FIELDS.FILE_UPLOAD.REMOVE_FILE')"
              @click="removeFile"
            />
          </div>

          <div
            v-else-if="isRecordingAudio"
            class="rounded-lg border border-n-weak p-3"
          >
            <p class="text-sm text-n-slate-12">
              {{
                t(
                  'CAMPAIGN.ENHANCED.FIELDS.FILE_UPLOAD.RECORDING_IN_PROGRESS',
                  {
                    duration: recordingAudioDurationText,
                  }
                )
              }}
            </p>
            <CampaignAudioRecorder
              ref="audioRecorderInput"
              @recorder-progress-changed="onRecorderProgressChanged"
              @finish-record="onFinishRecorder"
            />
            <div class="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Button
                class="w-full"
                variant="solid"
                size="sm"
                type="button"
                icon="square"
                :label="
                  t('CAMPAIGN.ENHANCED.FIELDS.FILE_UPLOAD.STOP_RECORDING')
                "
                @click="stopAudioRecording"
              />
              <Button
                class="w-full"
                variant="faded"
                size="sm"
                type="button"
                :label="t('CAMPAIGN.ENHANCED.BUTTONS.CANCEL')"
                @click="cancelAudioRecording"
              />
            </div>
          </div>

          <input
            v-else
            ref="fileInput"
            type="file"
            :accept="fileAccept"
            class="block w-full text-sm text-n-slate-11 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-n-blue-9 file:text-white hover:file:bg-n-blue-10"
            @change="handleFileUpload"
          />

          <p v-if="isUploadingFile" class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.ENHANCED.FIELDS.FILE_UPLOAD.UPLOADING') }}
          </p>

          <p class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.ENHANCED.FIELDS.FILE_UPLOAD.HELP') }}
          </p>
        </div>
      </div>

      <div class="hidden flex-col gap-2 md:flex">
        <Button
          variant="ghost"
          size="sm"
          type="button"
          icon="arrow-up"
          :disabled="!canMoveUp"
          @click="emit('move-up')"
        />
        <Button
          variant="ghost"
          size="sm"
          type="button"
          icon="arrow-down"
          :disabled="!canMoveDown"
          @click="emit('move-down')"
        />
      </div>
    </div>
  </div>
</template>
